<template>
  <div class="signin" v-if="!externalAuth">
    <el-card>
      <h2>{{ $t("signin.title") }}</h2>
      <el-form
        class="signin-form"
        :model="userForm"
        :rules="userRules"
        ref="userForm"
      >
        <el-form-item prop="username">
          <el-input
            v-model="userForm.username"
            :placeholder="$t('signin.userForm.username.placeholder')"
            prefix-icon="fas fa-user"
            clearable
          >
          </el-input>
        </el-form-item>

        <el-form-item prop="password">
          <el-input
            v-model="userForm.password"
            :placeholder="$t('signin.userForm.password.placeholder')"
            type="password"
            prefix-icon="fas fa-lock"
            clearable
          >
          </el-input>
        </el-form-item>

        <el-form-item prop="language" v-show="languageShow">
          <el-select
            v-model="userForm.language"
            clearable
            filterable
            :placeholder="$t('signin.userForm.language.placeholder')"
          >
            <template slot="prefix"><i class="fas fa-globe"></i></template>
            <el-option
              v-for="item in languages"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="!this.authServer"
          prop="connection"
          v-show="connectionShow"
        >
          <el-select
            v-model="userForm.connection"
            clearable
            filterable
            :placeholder="$t('signin.userForm.connection.placeholder')"
          >
            <template slot="prefix"><i class="fas fa-link"></i></template>
            <el-option
              v-for="item in connections"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item v-if="this.authServer" prop="server">
          <el-select
            v-model="userForm.server"
            clearable
            filterable
            :placeholder="$t('signin.userForm.server.placeholder')"
            :disabled="serverDisabled"
          >
            <template slot="prefix"><i class="fas fa-server"></i></template>
            <el-option
              v-for="item in authServers"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item v-if="this.authServer" prop="section">
          <el-select
            v-model="userForm.section"
            clearable
            filterable
            :placeholder="$t('signin.userForm.section.placeholder')"
            :disabled="sectionDisabled"
          >
            <template slot="prefix"><i class="fas fa-clone"></i></template>
            <el-option
              v-for="item in authSections"
              :key="item.section"
              :label="item.section"
              :value="item.section"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item v-if="this.authServer" prop="base">
          <el-select
            v-model="userForm.base"
            clearable
            filterable
            :placeholder="$t('signin.userForm.base.placeholder')"
            :disabled="baseDisabled"
          >
            <template slot="prefix"><i class="fas fa-database"></i></template>
            <el-option
              v-for="item in authBases"
              :key="item.base"
              :label="item.base"
              :value="item.base"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button
            class="login-button"
            type="primary"
            @click="signinSubmit"
            v-shortkey="{ Enter: ['enter'] }"
            @shortkey.native="onShortKeyRouter"
            block
            >{{ $t("signin.userForm.signin") }}</el-button
          >
        </el-form-item>
      </el-form>
    </el-card>
    <div class="version">
      {{ $t("signin.version.current") }} {{ webVersion }},
      {{ $t("signin.version.serverMin") }} {{ minVersions.server }},
      {{ $t("signin.version.apiMin") }} {{ minVersions.api }}
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { version, minVersions } from "../../package.json";
import { getHost } from "@/helpers/url";

export default {
  name: "signin",
  data() {
    return {
      userForm: {
        username: "",
        password: "",
        language: "",
        connection: "",
        server: "",
        section: "",
        base: ""
      },
      userRules: {
        username: [
          {
            required: true,
            message: this.$t("signin.userRules.username.required"),
            trigger: "blur"
          },
          {
            min: 3,
            message: this.$t("signin.userRules.username.min"),
            trigger: "blur"
          },
          {
            max: 24,
            message: this.$t("signin.userRules.username.max"),
            trigger: "blur"
          }
        ],
        password: [
          {
            required: true,
            message: this.$t("signin.userRules.password.required"),
            trigger: "blur"
          },
          {
            min: 3,
            message: this.$t("signin.userRules.password.min"),
            trigger: "blur"
          },
          {
            max: 24,
            message: this.$t("signin.userRules.password.max"),
            trigger: "blur"
          }
        ],
        language: [
          {
            required: true,
            message: this.$t("signin.userRules.language.required"),
            trigger: "blur"
          }
        ],
        connection: [
          {
            required: true,
            message: this.$t("signin.userRules.connection.required"),
            trigger: "blur"
          }
        ],
        server: [
          {
            required: true,
            message: this.$t("signin.userRules.server.required"),
            trigger: "blur"
          }
        ],
        section: [
          {
            required: true,
            message: this.$t("signin.userRules.section.required"),
            trigger: "blur"
          }
        ],
        base: [
          {
            required: true,
            message: this.$t("signin.userRules.base.required"),
            trigger: "blur"
          }
        ]
      },
      languages: [],
      connections: [],
      versions: {},
      serverDisabled: false,
      sectionDisabled: false,
      baseDisabled: false,
      storedServer: "",
      storedSection: "",
      storedBase: "",
      languageShow: true,
      connectionShow: true,
      authServer: false,
      externalAuth: true,
      version,
      minVersions
    };
  },
  async created() {
    document.title = this.$t("signin.title");

    await this.$store.dispatch("getConfig");

    this.languages = [...this.config.languages];
    this.connections = [...this.config.connections];

    // принудительная установка по умолчанию первого языка из config.json
    const firstLanguageConfig = this.languages[0].value;
    const verifiedLocale =
      firstLanguageConfig === "uk" ? "ua" : firstLanguageConfig;
    this.$i18n.locale = verifiedLocale;
    this.$moment.locale(verifiedLocale);

    let fillLanguage = "";
    if (this.languages.length === 1) {
      fillLanguage = this.languages[0].value;
      this.languageShow = false;
    }

    let fillConnection = "";
    if (this.connections.length === 1) {
      fillConnection = this.connections[0].value;
      this.connectionShow = false;
    }

    const savedConnection = this.$localStorage.get("wb-connection");
    if (savedConnection) {
      const connect = JSON.parse(savedConnection);
      this.userForm.username = connect.login || "";
      const langTransform = connect.language === "uk" ? "ua" : connect.language;
      this.userForm.language = langTransform || fillLanguage;
      this.userForm.connection = connect.connection || fillConnection;
      this.storedServer = connect.server || "";
      this.storedSection = connect.section || "";
      this.storedBase = connect.base || "";
    } else {
      this.userForm.username = "";
      this.userForm.language = fillLanguage;
      this.userForm.connection = fillConnection;
    }

    // принудительная установка по умолчанию в форме первого языка из списка
    this.userForm.language = verifiedLocale;

    if (this.config && this.config.hasOwnProperty("authserver")) {
      this.$store.commit("setAuthUrl", this.config.authserver);
      await this.$store.dispatch("getServers");
      this.authServer = true;
    } else {
      this.$store.commit("setAuthUrl", null);
      this.authServer = false;
    }

    this.externalAuth =
      this.config &&
      this.config.hasOwnProperty("externalauth") &&
      this.config.externalauth === "1"
        ? true
        : false;
    this.$store.commit("setExternalAuth", this.externalAuth);

    if (this.externalAuth) window.location.href = `${getHost()}signin/`;
  },
  computed: {
    ...mapGetters(["config", "authServers", "authSections", "authBases"]),
    webVersion: function() {
      const versionPackage = version.split(".");
      return `${versionPackage[1]}.${versionPackage[2]}`;
    }
  },
  watch: {
    authServers: function(servers) {
      if (servers) {
        if (this.storedServer) {
          const checkServer = servers.filter(
            item => item.id === this.storedServer
          );
          if (checkServer.length === 1) {
            this.userForm.server = checkServer[0].id;
            this.storedServer = "";
          }
        }
        if (servers.length === 1) {
          this.userForm.server = servers[0].id;
          this.serverDisabled = true;
        } else {
          this.serverDisabled = false;
        }
      } else {
        this.$store.commit("setAuthSections", null);
        this.$store.commit("setAuthBases", null);
        this.userForm.section = "";
        this.userForm.base = "";
      }
    },
    authSections: function(sections) {
      if (sections) {
        if (this.storedSection) {
          const checkSection = sections.filter(
            item => item.section === this.storedSection
          );
          if (checkSection.length === 1) {
            this.userForm.section = checkSection[0].section;
            this.storedSection = "";
          }
        }
        if (sections.length === 1) {
          this.userForm.section = sections[0].section;
          this.sectionDisabled = true;
        } else {
          this.sectionDisabled = false;
        }
      } else {
        this.$store.commit("setAuthBases", null);
        this.userForm.base = "";
      }
    },
    authBases: function(bases) {
      if (bases) {
        if (this.storedBase) {
          const checkBase = bases.filter(item => item.base === this.storedBase);
          if (checkBase.length === 1) {
            this.userForm.base = checkBase[0].base;
            this.storedBase = "";
          }
        }
        if (bases.length === 1) {
          this.userForm.base = bases[0].base;
          this.baseDisabled = true;
        } else {
          this.baseDisabled = false;
        }
      }
    },
    "userForm.server": async function(serverid) {
      if (serverid) {
        await this.$store.dispatch("getSections", { serverid });
      } else {
        this.$store.commit("setAuthSections", null);
        this.userForm.section = "";
        this.userForm.base = "";
      }
    },
    "userForm.section": async function(section) {
      this.userForm.base = "";
      const payload = section ? section : null;
      this.$store.commit("setAuthBases", payload);
    }
  },
  methods: {
    signinSubmit() {
      this.$refs.userForm.validate(valid => {
        if (valid) {
          this.$i18n.locale = this.userForm.language;
          const locale =
            this.userForm.language === "ua" ? "uk" : this.userForm.language;
          this.$moment.locale(locale);

          let url, label, connection, start;
          if (this.authServer) {
            const selectBase = this.authBases.filter(
              item => item.base === this.userForm.base
            );
            url = selectBase[0].url;
            label = selectBase[0].base;
            connection = "";
            start = "temporary"; // временно, для авторизации через web-admin надо будет отладить
          } else {
            const selectConnection = this.connections.filter(
              item => item.value === this.userForm.connection
            );
            url = selectConnection[0].url;
            label = selectConnection[0].label;
            connection = selectConnection[0].value;
            start = selectConnection[0].hasOwnProperty("start") // транзитный ключ start, нужен для веб-отчетов
              ? selectConnection[0].start
              : "";
          }

          this.$store.dispatch("signIn", {
            login: this.userForm.username,
            password: this.userForm.password,
            language: this.userForm.language,
            connection,
            url,
            label,
            start,
            server: this.userForm.server,
            section: this.userForm.section,
            base: this.userForm.base,
            external: this.externalAuth
          });
        }
      });
    },
    onShortKeyRouter(event) {
      if (event.srcKey == "Enter") {
        this.signinSubmit();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.signin {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.signin .el-card {
  width: 340px;
  display: flex;
  justify-content: center;
}

.signin .version {
  margin-top: 5px;
  font-size: 0.8em;
  color: rgb(126, 126, 126);
  width: 340px;
  display: flex;
  justify-content: center;
}

.signin-form {
  width: 290px;
}

.el-input {
  width: 100%;
}

.el-select {
  width: 100%;
}

.el-button {
  width: 100%;
}

h2 {
  letter-spacing: 1px;
  text-align: center;
}
</style>
